import _defineProperty from "@babel/runtime/helpers/defineProperty";
/*
 *                     NOTICE
 *            COPYRIGHT 2021 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */

import { LogUtils } from "abweb/utils/log/LogUtils";
import { ProfileUtils } from "abweb/utils/profile/ProfileUtils";
const LOG = LogUtils.getLogger(__filename);

// NOTE: there is a 'raw js' version of this that is used by the index.jsp login workflow, see
//          abweb\assets\js\appLoader\mutex.js
//       If there are any bugs, please fix in both locations.

/**
 * Provide support to ensure that the asyncFn running within a runExclusive invocation is the only one running within
 * the scope of a Mutex instance.  The runExclusion invocations are effectively stored in q queue, guaranteeing
 * FIFO ordering of corresponding asyncFn invocations.
 *
 * @author Brad Miller
 */
export class Mutex {
  /**
   * @param debug default false.  If true, will log when lock is requested, acquired, and released using the 'logName'
   * argument to the runExclusive method.
   */
  constructor(debug = false) {
    // Largely inspired by:
    //   https://stackoverflow.com/questions/51086688/mutex-in-javascript-does-this-look-like-a-correct-implementation
    _defineProperty(this, "isDebug", void 0);
    _defineProperty(this, "lastLock", void 0);
    this.isDebug = debug;
    // _lock points to last lock caller in implicit promise chain of lock callers
    this.lastLock = Promise.resolve();
  }

  /**
   * Build releasable promise that will be released when caller invokes returned release function,
   * and set the new releasable promise to be the this._lastLock value (implicit 'end' of promise chain)
   */
  appendLockPromise() {
    // Conceptually you have lockQueue (promise 'then' chain created by first line in 'lock' method):
    //    lockQueue:    emptyP -> pA  -> pB -> pC -> ...
    // but as you always only need access to the 'last' promise in the chain in order to append the newest lock request,
    // it just keeps track of the 'last' promise in the chain via the 'lastLock' variable
    // (which also allows better garbage collection of promises!)
    //    lastLock:    iteratively update to the following values:
    //      emptyP
    //      pA
    //      pB
    //      pC
    //      ...
    let releaseLockFn;
    this.lastLock = new Promise(resolve => {
      releaseLockFn = resolve;
    });
    return releaseLockFn;
  }

  /**
   * The async lock promise resolves when the lock is acquired.
   * Typically use runExclusive instead of invoking this directly
   * @returns Promise<releaseLockFn>, and caller must invoke releaseLockFn() when the lock critical section is finished
   */
  lock() {
    const currLastLock = this.lastLock;
    // Now make this caller the new 'last lock' so that subsequent lock callers will be queued behind it
    const releaseLockFn = this.appendLockPromise();

    // Register to be invoked when upstream lock is released, and return 'releaseLockFn' for new promise when it is
    return currLastLock.then(() => releaseLockFn);
  }

  /**
   * Runs asyncFn in critical section when it obtains the lock, and releases the lock when asyncFn has completed.
   *
   * @param logName Pretty name describing asyncFn that is used in logged output.  It also uses the logName
   * as a key for storing the profile stats of the asyncFn execution.  Stats are available in the PFS and
   * typically in the UI via the 'Help -> Debugging Tools -> Client Profiling Statistics', as well as programmatically
   * via the ProfileUtils class.
   * 
   * @param asyncFn asynchronous function that will run within critical section while lock is acquired
   */
  async runExclusive(logName, asyncFn) {
    this.log(logName, "requests lock");
    const releaseLockFn = await this.lock();
    try {
      this.log(logName, "acquired lock");
      // Use await so that releaseLockFn() called AFTER asyncFn completes
      // Use ProfileUtils so that timing stats shows up in pfs and 'Client Profiling Statistics' dialog
      return await ProfileUtils.elapsedAsync(logName, asyncFn);
    } finally {
      releaseLockFn();
      this.log(logName, "released lock");
    }
  }
  log(logName, msg) {
    // During jasmine test the default log level is 'info'
    // so can not use LOG.debug if need to examine logged output within a test (e.g., MutexUSpec test)
    this.isDebug && LOG.info(`'${logName}' ${msg}`);
  }
}
//# sourceMappingURL=Mutex.js.map
